import React, { useEffect, useRef, useState, useCallback } from 'react';
import CountdownOverlay from './CountdownOverlay';
import { BeatstagePlayer } from '../playBeatstage';
import { GameClock } from '../timing';

interface Props {
  encoded: string;
  audio: Blob | string;
  showAll?: boolean;
}

/**
 * Playback component that renders a Beatstage chart using the game's logic.
 * A scrubber allows seeking freely within the song.
 */
export default function BeatstagePlayback({ encoded, audio, showAll = false }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<BeatstagePlayer | null>(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [countdown, setCountdown] = useState<{
    ctx: AudioContext;
    startAt: number;
    beatDuration: number;
  } | null>(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !encoded || !audio) return;
    let cancelled = false;

    const setup = async () => {
      const player = await BeatstagePlayer.create(encoded, audio, container, showAll);
      if (cancelled) {
        player.destroy();
        return;
      }
      playerRef.current = player;
      setDuration(player.duration);
      const ctx = GameClock.context!;
      if (ctx.state === 'suspended') await ctx.resume();
      const beatDuration = 1;
      const startAt = ctx.currentTime + 3 * beatDuration;
      setCountdown({ ctx, startAt, beatDuration });
    };
    void setup();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafRef.current);
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, [encoded, audio, showAll]);

  const startPlayback = useCallback(async () => {
    const player = playerRef.current;
    if (!player) return;
    await player.playFrom(0);
    const tick = () => {
      setCurrentTime(player.currentTime);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const handleCountdownComplete = useCallback(() => {
    setCountdown(null);
    void startPlayback();
  }, [startPlayback]);

  const onScrub = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = Number(e.target.value);
    setCurrentTime(t);
    void playerRef.current?.playFrom(t);
  };

  return (
    <div ref={containerRef} className="relative h-full w-full bg-black">
      {countdown && (
        <CountdownOverlay
          ctx={countdown.ctx}
          startAt={countdown.startAt}
          beatDuration={countdown.beatDuration}
          onComplete={handleCountdownComplete}
        />
      )}
      {duration > 0 && (
        <input
          type="range"
          min={0}
          max={duration}
          step={0.001}
          value={currentTime}
          onChange={onScrub}
          className="absolute bottom-2 left-2 right-2"
        />
      )}
    </div>
  );
}
